import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
    ShieldCheck, ArrowLeft, Loader2, Users, Radio, Inbox, AlarmClock,
    UserPlus, MessageSquare, MessageSquareText, Clock, Search, Eye, Sparkles, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const POLL_MS = 60_000;

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT"));

function TrendBadge({ pct }) {
    if (pct == null) return null;
    const positive = pct > 0;
    const zero = pct === 0;
    const Icon = zero ? Minus : positive ? TrendingUp : TrendingDown;
    const cls = zero
        ? "text-slate-500 bg-slate-100"
        : positive
            ? "text-emerald-700 bg-emerald-50"
            : "text-rose-700 bg-rose-50";
    const sign = positive ? "+" : "";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
            <Icon className="size-3" />
            {sign}{pct}%
        </span>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone = "default", testId }) {
    const tones = {
        default: "border-slate-200 bg-white",
        primary: "border-primary/25 bg-primary/[0.04]",
        warning: "border-amber-300 bg-amber-50",
        danger: "border-rose-300 bg-rose-50",
    };
    return (
        <div className={`rounded-2xl border ${tones[tone]} p-5`} data-testid={testId}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
                {Icon && <Icon className="size-3.5" />} {label}
            </div>
            <div className="mt-2 font-serif text-4xl font-semibold text-slate-900 leading-none">{value}</div>
            {sub && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
        </div>
    );
}

function SectionTitle({ icon: Icon, children }) {
    return (
        <div className="mt-10 mb-4 flex items-center gap-2">
            {Icon && <Icon className="size-4 text-primary" />}
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">{children}</div>
        </div>
    );
}

export default function AdminAnalyticsUsage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshingAt, setRefreshingAt] = useState(null);

    const load = async () => {
        try {
            const { data } = await API.get("/admin/analytics/overview");
            setData(data);
            setRefreshingAt(new Date());
            setError("");
        } catch (e) {
            setError(apiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastRefresh = useMemo(() => {
        if (!refreshingAt) return "";
        const secs = Math.max(0, Math.round((Date.now() - refreshingAt.getTime()) / 1000));
        if (secs < 60) return `aggiornato ${secs}s fa`;
        return `aggiornato ${Math.round(secs / 60)} min fa`;
    }, [refreshingAt, data]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-3">
                <h1 className="font-serif text-3xl font-semibold" data-testid="analytics-title">Statistiche di utilizzo</h1>
                <span className="text-xs text-muted-foreground" data-testid="analytics-refresh-info">
                    {loading && !data ? "caricamento…" : lastRefresh || ""} · aggiornamento auto ogni 60s
                </span>
            </div>
            <p className="mt-2 text-muted-foreground text-sm">
                Panoramica del traffico e dell'utilizzo reale della piattaforma. Solo dati aggregati, nessun PII.
            </p>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}
            {loading && !data && (
                <div className="mt-10 text-center py-14"><Loader2 className="size-6 animate-spin mx-auto text-muted-foreground" /></div>
            )}

            {data && (
                <>
                    <SectionTitle icon={Radio}>In questo momento</SectionTitle>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                        <StatCard icon={Radio} tone="primary" label="Utenti online adesso" value={fmt(data.online_now)} sub="Attivi negli ultimi 10 minuti" testId="stat-online-now" />
                        <StatCard icon={Inbox} tone={data.pending_questions > 0 ? "warning" : "default"} label="Domande in attesa" value={fmt(data.pending_questions)} testId="stat-pending" />
                        <StatCard icon={AlarmClock} tone={data.stale_questions_24h > 0 ? "danger" : "default"} label="Non evase da oltre 24h" value={fmt(data.stale_questions_24h)} testId="stat-stale" />
                    </div>

                    <SectionTitle icon={Users}>Visitatori (unici, include anonimi)</SectionTitle>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                        <StatCard label="Ultime 24 ore" value={fmt(data.visitors.h_24)} testId="stat-visitors-24h" />
                        <StatCard label="Ultimi 7 giorni" value={fmt(data.visitors.d_7)} testId="stat-visitors-7d" />
                        <StatCard label="Ultimi 30 giorni" value={fmt(data.visitors.d_30)} testId="stat-visitors-30d" />
                    </div>

                    <SectionTitle icon={UserPlus}>Utenti registrati</SectionTitle>
                    <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
                        <StatCard label="Totali" value={fmt(data.registered.total)} testId="stat-users-total" />
                        <StatCard label="Nuovi oggi" value={fmt(data.registered.new_today)} testId="stat-users-today" />
                        <StatCard label="Nuovi questa settimana" value={fmt(data.registered.new_week)} testId="stat-users-week" />
                        <StatCard label="Nuovi questo mese" value={fmt(data.registered.new_month)} testId="stat-users-month" />
                    </div>
                    <div className="mt-3 grid gap-3 grid-cols-1 sm:grid-cols-3">
                        <StatCard label="Attivi loggati · 24h" value={fmt(data.registered.active_24h)} testId="stat-users-active-24h" />
                        <StatCard label="Attivi loggati · 7g" value={fmt(data.registered.active_7d)} testId="stat-users-active-7d" />
                        <StatCard label="Attivi loggati · 30g" value={fmt(data.registered.active_30d)} testId="stat-users-active-30d" />
                    </div>

                    <SectionTitle icon={MessageSquare}>Domande e risposte</SectionTitle>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stat-questions">
                            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                                <MessageSquare className="size-3.5" /> Domande inviate
                            </div>
                            <div className="mt-3 space-y-1.5 text-sm text-slate-700">
                                <div>Oggi: <strong className="text-slate-900 text-base">{fmt(data.questions.today)}</strong></div>
                                <div>Settimana: <strong className="text-slate-900 text-base">{fmt(data.questions.week)}</strong></div>
                                <div>Mese: <strong className="text-slate-900 text-base">{fmt(data.questions.month)}</strong></div>
                            </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stat-answers">
                            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                                <MessageSquareText className="size-3.5" /> Risposte inviate
                            </div>
                            <div className="mt-3 space-y-1.5 text-sm text-slate-700">
                                <div>Oggi: <strong className="text-slate-900 text-base">{fmt(data.answers.today)}</strong></div>
                                <div>Settimana: <strong className="text-slate-900 text-base">{fmt(data.answers.week)}</strong></div>
                                <div>Mese: <strong className="text-slate-900 text-base">{fmt(data.answers.month)}</strong></div>
                            </div>
                        </div>
                        <StatCard icon={Clock} tone="primary" label="Tempo medio di risposta" value={data.avg_response.human || "—"} sub="Su tutte le risposte fornite" testId="stat-avg-response" />
                    </div>

                    <SectionTitle icon={Search}>Interesse degli utenti</SectionTitle>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 overflow-hidden" data-testid="stat-top-searched">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <Search className="size-4 text-primary" /> Top 10 FAQ più cercate
                                <span className="text-[11px] font-normal text-muted-foreground">(30gg, raggruppate)</span>
                            </div>
                            {data.top_searched.length === 0 ? (
                                <div className="mt-4 text-sm text-muted-foreground">Nessuna ricerca registrata nel periodo.</div>
                            ) : (
                                <Accordion type="multiple" className="mt-3 space-y-1">
                                    {data.top_searched.map((row, i) => (
                                        <AccordionItem key={row.faq_id} value={row.faq_id} className="border rounded-lg border-slate-200 overflow-hidden">
                                            <AccordionTrigger className="px-3 py-2 hover:no-underline text-left w-full min-w-0 [&>svg]:shrink-0">
                                                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-xs text-muted-foreground shrink-0 w-5 text-right">{i + 1}.</span>
                                                        <span className="font-medium text-slate-900 truncate">{row.title}</span>
                                                    </div>
                                                    <div className="mt-1 sm:mt-0 sm:ml-auto flex items-center gap-2 pl-7 sm:pl-0 shrink-0">
                                                        <span className="text-xs text-slate-500">{fmt(row.count)} ricerche</span>
                                                        <TrendBadge pct={row.trend_pct} />
                                                    </div>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-3 pb-3">
                                                {row.top_formulations.length === 0 ? (
                                                    <div className="text-xs text-muted-foreground">Nessuna formulazione registrata.</div>
                                                ) : (
                                                    <ul className="mt-1 space-y-1 text-xs text-slate-600">
                                                        {row.top_formulations.map((f, idx) => (
                                                            <li key={idx} className="flex items-baseline gap-2">
                                                                <span className="text-slate-400 shrink-0">›</span>
                                                                <span className="italic break-words min-w-0">"{f.q}"</span>
                                                                <span className="ml-auto text-slate-500 shrink-0">{fmt(f.n)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stat-top-viewed">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <Eye className="size-4 text-primary" /> Top 10 FAQ più viste
                                <span className="text-[11px] font-normal text-muted-foreground">(30gg)</span>
                            </div>
                            {data.top_viewed.length === 0 ? (
                                <div className="mt-4 text-sm text-muted-foreground">Nessuna visualizzazione registrata.</div>
                            ) : (
                                <ol className="mt-3 space-y-1 text-sm">
                                    {data.top_viewed.map((row, i) => (
                                        <li key={row.faq_id} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50">
                                            <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
                                            <span className="flex-1 min-w-0 truncate text-slate-900">{row.title}</span>
                                            <span className="text-xs text-slate-500 shrink-0">{fmt(row.count)}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="stat-top-pills">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <Sparkles className="size-4 text-primary" /> Top 10 suggerimenti "Prova una ricerca" cliccati
                                <span className="text-[11px] font-normal text-muted-foreground">(30gg)</span>
                            </div>
                            {data.top_pills.length === 0 ? (
                                <div className="mt-4 text-sm text-muted-foreground">Nessun click sulle pill registrato nel periodo.</div>
                            ) : (
                                <ol className="mt-3 grid gap-1 sm:grid-cols-2">
                                    {data.top_pills.map((row, i) => (
                                        <li key={i} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50">
                                            <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
                                            <span className="flex-1 min-w-0 truncate italic text-slate-800">"{row.text}"</span>
                                            <span className="text-xs text-slate-500 shrink-0">{fmt(row.count)}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
